import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { TimeUtils } from '@freelancer/time-utils';
import {
  CalloutColor,
  CalloutComponent,
  CalloutPlacement,
  CalloutSize,
} from '../callout';
import { HighlightCalloutComponent } from './highlight-callout.component';

@Component({
  selector: 'fl-highlight',
  template: `
    <div class="HighlightContent">
      <fl-callout
        #highlightCallout
        *ngIf="calloutComponent; else highlightContent"
        [hideCloseButton]="hideCloseButton"
        [placement]="calloutPlacement"
        [color]="calloutColor"
        [size]="calloutSize"
        [edgeToEdge]="calloutEdgeToEdge"
        [mobileCloseButton]="mobileCloseButton"
        (calloutClose)="handleCalloutClose()"
      >
        <!--
          This trigger is always [disabled] because the callout should
          only be opened programmatically via this component, not on click.
        -->
        <fl-callout-trigger
          class="HighlightCalloutTrigger"
          [disabled]="true"
        >
          <ng-container *ngTemplateOutlet="highlightContent"></ng-container>
        </fl-callout-trigger>
        <fl-callout-content>
          <ng-content select="fl-highlight-callout"></ng-content>
        </fl-callout-content>
      </fl-callout>
    </div>

    <ng-template #highlightContent>
      <ng-content></ng-content>
    </ng-template>
  `,
  styleUrls: ['./highlight.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HighlightComponent implements OnChanges {
  @HostBinding('class.IsHighlighted')
  @Input()
  apply = false;
  @Input() calloutPlacement = CalloutPlacement.RIGHT;
  @Input() calloutColor = CalloutColor.LIGHT;
  @Input() calloutSize = CalloutSize.SMALL;
  @Input() calloutEdgeToEdge = false;
  @Input() hideCloseButton = true;
  @Input() mobileCloseButton = true;

  @Output() calloutClose = new EventEmitter<void>();

  @ViewChild('highlightCallout')
  highlightCallout: CalloutComponent;

  @ContentChild(HighlightCalloutComponent, { static: true })
  calloutComponent: HighlightCalloutComponent;

  // FIXME: T300116 Enabling hydration errors on the webapp server
  // TypeError: Cannot read properties of null (reading 'parentElement')
  @HostBinding('attr.ngSkipHydration') skipHydration = 'true';

  constructor(private timeUtils: TimeUtils) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('apply' in changes && this.calloutComponent) {
      this.timeUtils.setTimeout(() => {
        if (!this.highlightCallout) {
          return;
        }

        if (this.apply) {
          this.highlightCallout.open();
        } else {
          this.highlightCallout.close();
        }
      });
    }
  }

  handleCalloutClose(): void {
    this.calloutClose.emit();
  }

  @HostListener('click')
  onClick(): void {
    // We should hide the callout whenever the user
    // clicked outside and apply is true.
    if (this.apply && this.highlightCallout) {
      this.highlightCallout.close();
    }
  }
}
